import {FreeDeliverySection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/basket_mov.types';
import {SizeSelection_Size} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/size_selection.types';
import {AddItemResponseStatus_Success_Section} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/add_item_status.types';
import {useTranslation} from '@aboutyou/i18n';
import {useBasketRoute, useHistory} from '@aboutyou/router';
import {useTrackOriginClick} from '@aboutyou/tracking';
import {
  BasketFlyoutSuccessButtons,
  COLORS,
  Flyout,
  FlyoutBody,
  GenericIconName,
  Icon,
  Text,
} from '@aboutyou/ui-components';
import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {useSizesSection} from '../../backend-services/ArticleDetail/useGetProductBulkSection';
import {useTrackMobileAdpFeature} from '../../backend-services/ArticleDetail/utils/useTrackMobileAdpFeature';
import {getBasketUrgencyFromSections} from '../BasketUrgencyHint/utils/getBasketUrgencyFromSections';
import {getPromotionInvalidFromSections} from '../BasketUrgencyHint/utils/getPromotionInvalidSection';
import {
  SizeSelectionOpenedStateKey,
  useSizeSelectionOpenedState,
} from '../SizeSelectionFlyout/hooks/useSizeSelectionOpenedState';
import {AddToBasketSuccessFlyoutContent} from './AddToBasketFlyoutContent/AddToBasketSuccessFlyoutContent';
import {useBasketRecommendations} from './hooks/useBasketRecommendations';

interface AddToBasketSuccessFlyoutWithRecosProps extends SuccessFlyoutProps {
  sizeSelectionKey: SizeSelectionOpenedStateKey | string;
  productId: number;
  freeDeliverySection?: FreeDeliverySection;
  statusSuccessSections?: AddItemResponseStatus_Success_Section[];
}

interface SuccessFlyoutProps {
  onClose: VoidFunction;
  show: boolean;
  renderInPortal?: boolean;
  trackOriginClick?: VoidFunction;
  className?: string;
  selectedSize: SizeSelection_Size | undefined;
}
export const AddToBasketSuccessFlyoutWithRecos: React.FC<
  AddToBasketSuccessFlyoutWithRecosProps
> = ({
  onClose,
  show,
  sizeSelectionKey,
  productId,
  renderInPortal,
  freeDeliverySection,
  statusSuccessSections = [],
  className,
  selectedSize,
}) => {
  const history = useHistory();
  const basketRoute = useBasketRoute();
  const {trackMobileAdpFeature} = useTrackMobileAdpFeature();
  const {isOpen: isSizeSelectionOpen} =
    useSizeSelectionOpenedState(sizeSelectionKey);
  const onClickPrimaryButton = () => {
    onClose();

    trackMobileAdpFeature?.({
      eventType: 'click',
      label: 'adp|a2b|continueShopping',
    });
  };

  const onClickSecondaryButton = () => {
    trackOriginClick?.();
    trackMobileAdpFeature?.({
      eventType: 'click',
      label: 'adp|a2b|goToBasket',
    });

    // Replace the open success flyout state so if the user click history back on the browser they are redirected to the ADP without the success flyout opened.
    history.replaceWithScrollToTop(basketRoute);
  };

  const [preloadBasketRecommendations, setPreloadBasketRecommendations] =
    useState(isSizeSelectionOpen || Boolean(selectedSize));

  const trackOriginClick = useTrackOriginClick()(
    'add_to_basket_complemetary_reco',
  );

  useEffect(() => {
    if (!preloadBasketRecommendations && selectedSize) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setPreloadBasketRecommendations(true);
    }
  }, [
    selectedSize,
    setPreloadBasketRecommendations,
    preloadBasketRecommendations,
  ]);

  // preload basket recommendations
  // Basket recommendations are in flyout and preload will do nothing there as request will trigger only after
  // flyout is already opened and at that time its late
  useBasketRecommendations({
    productId,
    requestEnabled: preloadBasketRecommendations,
  });

  const sizesSectionData = useSizesSection();

  useEffect(() => {
    const isSingleSize =
      sizesSectionData.data?.sizeSelection.type?.$case === 'oneSize';

    if (isSizeSelectionOpen || isSingleSize) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect -- TODO: Cleanup setState in useEffect https://aboutyou.atlassian.net/browse/ASFE-7602
      setPreloadBasketRecommendations(true);
    }
  }, [setPreloadBasketRecommendations, isSizeSelectionOpen, sizesSectionData]);

  const basketUrgency = getBasketUrgencyFromSections(statusSuccessSections);
  const promotionInvalidSection = getPromotionInvalidFromSections(
    statusSuccessSections,
  );

  const ctas = (
    <PaddingWrapper>
      <BasketFlyoutSuccessButtons
        onClickPrimaryButton={onClickPrimaryButton}
        onClickSecondaryButton={onClickSecondaryButton}
      />
    </PaddingWrapper>
  );

  return (
    <StyledFlyout
      className={className}
      onClose={onClose}
      open={show}
      hideCloseButton
      headerContent={
        <SuccessHeader
          withoutMarginBottom={Boolean(
            basketUrgency?.type?.$case === 'composedHint' ||
              promotionInvalidSection,
          )}
        />
      }
      renderInPortal={renderInPortal}
      bodyContent={
        <Content data-testid="addToBasketSuccessView">
          <AddToBasketSuccessFlyoutContent
            loadContent={preloadBasketRecommendations}
            productId={productId}
            trackOriginClick={trackOriginClick}
            ctas={ctas}
            freeDeliverySection={freeDeliverySection}
            statusSuccessSections={statusSuccessSections}
          />
        </Content>
      }
    />
  );
};

const StyledFlyout = styled(Flyout)`
  ${FlyoutBody} {
    padding: 0;
  }
`;

const Content = styled.article`
  overflow-y: auto;
`;

const PaddingWrapper = styled.div`
  padding: 1.25rem 1rem;
  border-bottom: 1px solid ${COLORS.Grey3};
`;

const IconWrapper = styled.div`
  padding: 4px;
  border-radius: 50%;
  background: ${COLORS.Green};
  width: 20px;
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SuccessHeaderContainer = styled.div<{$withoutMarginBottom?: boolean}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: ${props =>
    props.$withoutMarginBottom ? '3px 0 0 0' : '3px 0 10px 0'};
`;

const SuccessText = styled(Text)`
  margin-left: 10px;
`;

const SuccessHeader: React.FC<{withoutMarginBottom?: boolean}> = ({
  withoutMarginBottom,
}) => {
  const {t} = useTranslation();

  return (
    <SuccessHeaderContainer $withoutMarginBottom={withoutMarginBottom}>
      <IconWrapper>
        <Icon
          size="XS"
          asset={GenericIconName.Checkmark}
          style={{
            fill: COLORS.White,
          }}
        />
      </IconWrapper>
      <SuccessText size="px14">
        {t('Successfully added to your cart!')}
      </SuccessText>
    </SuccessHeaderContainer>
  );
};
